/**
 * The React-side half of the tag loader in public/index.html.
 *
 * That inline script decides two things before any tracker exists on the page
 * and publishes them on `window.__wpTracking`:
 *
 *   enabled  the host may talk to the real Google / Meta / Clarity accounts
 *            (production only, or a browser that opted in for QA)
 *   consent  the Consent Mode v2 state Cookiebot last reported, or null when
 *            the visitor has not chosen yet
 *
 * Everything here reads that object rather than deciding for itself, so there
 * is exactly one place where "may we track?" is answered. If you change the
 * host list or the consent mapping, change it in index.html.
 *
 * Every function is safe on the server (no `window`) and safe when the tag
 * loader has not run (very early render, or a test environment).
 */

/** Custom event the loader dispatches whenever Cookiebot reports a change. */
export const CONSENT_CHANGE_EVENT = 'wp:consentchange';

const getState = () => (typeof window !== 'undefined' ? window.__wpTracking : null) || null;

/**
 * True when tags are allowed to load and events are allowed to be sent at all.
 * False on staging, review apps and localhost — the guard that keeps internal
 * test orders out of the real Ads and Analytics accounts.
 */
export const isTrackingEnabled = () => !!getState()?.enabled;

/** True on a non-production host that opted in for QA — events carry debug_mode. */
export const isTrackingDebug = () => !!getState()?.debug;

/** `{ debug_mode: true }` for QA browsers, otherwise nothing to spread. */
export const debugParams = () => (isTrackingDebug() ? { debug_mode: true } : {});

/**
 * Consent for measurement (GA4, Clarity). Cookiebot's "statistics" category.
 */
export const hasAnalyticsConsent = () => getState()?.consent?.analytics_storage === 'granted';

/**
 * Consent for advertising (Google Ads, Meta Pixel, enhanced conversions).
 * Cookiebot's "marketing" category.
 */
export const hasMarketingConsent = () => getState()?.consent?.ad_storage === 'granted';

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 * Used by IncludeScripts to add the GA4 tag the moment consent is given.
 */
export const subscribeToConsent = callback => {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const handler = () => callback(getState()?.consent || null);
  window.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
};

/**
 * Reopen the consent banner so the visitor can change an earlier choice.
 *
 * Cookiebot stores the choice in its own cookie and `renew()` shows the banner
 * again with the current selection pre-filled; accepting or declining then runs
 * through the same updateConsent() path in public/index.html, so a withdrawn
 * consent takes effect immediately. Cookiebot also deletes the cookies it
 * blocked once consent is withdrawn.
 *
 * Returns false when the consent manager has not loaded (a blocked script, or a
 * very early click) so the caller can leave its control out of the page rather
 * than showing a button that does nothing.
 *
 * @return {Boolean} whether the banner was reopened
 */
export const openConsentSettings = () => {
  if (typeof window === 'undefined' || typeof window.Cookiebot?.renew !== 'function') {
    return false;
  }
  try {
    window.Cookiebot.renew();
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Send a gtag call, but only when the host gate and analytics consent both
 * allow it. Returns true when the call was made, which is what the tests
 * assert on.
 *
 * @param  {...any} args forwarded verbatim to window.gtag
 * @return {Boolean}
 */
export const gtagSafe = (...args) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return false;
  }
  if (!isTrackingEnabled() || !hasAnalyticsConsent()) {
    return false;
  }
  try {
    window.gtag(...args);
    return true;
  } catch (e) {
    // Analytics must never break the page.
    return false;
  }
};

/**
 * The Google Analytics client id for this browser, read out of the `_ga`
 * cookie. gtag writes it as `GA1.1.<a>.<b>`, where `<a>.<b>` is the client id.
 *
 * We read the cookie rather than calling `gtag('get', ...)` because that call
 * is asynchronous and this value is needed synchronously, while building the
 * checkout order parameters. It is stored on the transaction so the server's
 * purchase backstop can attribute its event to the same visitor session.
 *
 * @return {String|null}
 */
export const getGaClientId = () => {
  if (typeof document === 'undefined') {
    return null;
  }
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match) {
      return null;
    }
    // 'GA1.1.1234567890.1700000000' -> '1234567890.1700000000'
    const parts = decodeURIComponent(match[1]).split('.');
    return parts.length >= 4 ? parts.slice(-2).join('.') : null;
  } catch (e) {
    return null;
  }
};
